/**
 * Copyright 2025-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useMemo } from "react";
import type { ReactNode, MouseEvent } from "react";
import { When } from "react-if";
import {
  Box,
  Button,
  Checkbox,
  ExtendedPopover,
  FlexContainer,
  Text,
  theme,
} from "@nordcloud/gnui";

import { getUpdateReportStatusDisplay } from "../const";
import {
  type UpdateReportOverviewData,
  UpdateReportItemStatus,
} from "../types";

type Props = {
  details: UpdateReportOverviewData | null;
  selectedStatuses: UpdateReportItemStatus[];
  onChangeStatus: (statuses: UpdateReportItemStatus[]) => void;
  hasUpdates: boolean;
};

export function TableFilterPopover({
  details,
  selectedStatuses,
  onChangeStatus,
  hasUpdates,
}: Props) {
  const statusItems = useMemo(() => {
    if (!details) {
      return [];
    }

    return Object.entries(details.filterSummary.statusCounts)
      .map(([id, count]) => ({
        id: id as UpdateReportItemStatus,
        count,
      }))
      .filter(({ count }) => count > 0);
  }, [details]);

  const toggleStatus = (value: UpdateReportItemStatus) => {
    onChangeStatus(
      selectedStatuses.includes(value)
        ? selectedStatuses.filter((item) => item !== value)
        : [...selectedStatuses, value]
    );
  };

  const clearFilters = () => {
    onChangeStatus([]);
  };

  return (
    <>
      <ExtendedPopover
        placement="left"
        position="start"
        triggerOn="click"
        closeOn="click"
        closeOnScroll={false}
        zIndex={theme.zindex.topoftheworld}
        trigger={
          <Button
            severity="low"
            icon="filter"
            css={{ position: "relative" }}
            disabled={!hasUpdates}
          >
            Filters
          </Button>
        }
        content={
          <Box
            shadow="shadow04"
            innerSpacing="spacing04"
            css={{ minWidth: "240px", maxWidth: "280px" }}
            onClick={(e: MouseEvent) => e.stopPropagation()}
          >
            <FlexContainer direction="column" alignItems={"flex-start"}>
              <FilterSection title="Update Status">
                {statusItems.map(({ id, count }) => (
                  <FilterCheckbox
                    key={id}
                    label={getUpdateReportStatusDisplay(id).label}
                    count={count}
                    checked={selectedStatuses.includes(id)}
                    onChange={() => toggleStatus(id)}
                  />
                ))}
              </FilterSection>
            </FlexContainer>
            <FlexContainer
              justifyContent="center"
              css={{ marginTop: theme.spacing.spacing04 }}
            >
              <Button
                size="sm"
                severity="low"
                onClick={clearFilters}
                disabled={!selectedStatuses.length}
              >
                Clear filters
              </Button>
            </FlexContainer>
          </Box>
        }
      />
    </>
  );
}

function FilterSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <FlexContainer direction="column" alignItems="flex-start">
      <Text weight="medium">{title}</Text>
      <FlexContainer
        direction="column"
        alignItems="flex-start"
        gap={theme.spacing.spacing02}
      >
        {children}
      </FlexContainer>
    </FlexContainer>
  );
}

function FilterCheckbox({
  label,
  count,
  checked,
  onChange,
}: {
  label: string;
  count?: number;
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <FlexContainer
      alignItems="center"
      css={{ cursor: "pointer" }}
      onClick={onChange}
    >
      <div>
        <Checkbox
          checked={checked}
          onChange={onChange}
          onClick={(event) => event.stopPropagation()}
        />
      </div>
      <FlexContainer alignItems="center" gap={theme.spacing.spacing01}>
        <Text css={{ margin: 0 }}>{label}</Text>
        <When condition={count}>
          <Text css={{ margin: 0 }} color={theme.color.text.text02} size={"sm"}>
            ({count})
          </Text>
        </When>
      </FlexContainer>
    </FlexContainer>
  );
}
